/**
 * ChatSidebar - Collapsible chat sidebar for board conversations
 *
 * Implements:
 * - HOME-03: Chat sidebar with @ tagging for agents
 * - HOME-07: Streaming token-by-token responses
 * - HOME-10: File attachments support
 * - HOME-11: Video transcription via Workers AI
 * - HOME-12: RBAC-gated UI (permission checks)
 *
 * Features:
 * - Collapsible sidebar with smooth animation
 * - Message history with user/assistant messages
 * - Streaming response display
 * - File attachment previews
 * - Clear and close buttons
 * - Error handling with retry
 */

import { useRef, useEffect } from 'react';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { useChat } from '../../hooks/useChat';
import { usePermissions } from '../../hooks/usePermissions';

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface ChatSidebarProps {
  /** Board ID for scoping chat */
  boardId: string;
  /** Whether the sidebar is open */
  isOpen: boolean;
  /** Callback to toggle sidebar open/closed */
  onToggle: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ChatSidebar({ boardId, isOpen, onToggle }: ChatSidebarProps) {
  // Get RBAC permissions (HOME-12)
  const permissions = usePermissions();

  // Chat state and utilities
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    transcribe,
    stop,
    clear,
  } = useChat({
    boardId,
    onError: (err) => console.error('[ChatSidebar] Error:', err),
  });

  // Ref for auto-scroll to bottom
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // -------------------------------------------------------------------------
  // HANDLERS
  // -------------------------------------------------------------------------

  /**
   * Handle sending a message.
   * Passes message content, tagged agents, and files to useChat.
   */
  const handleSend = (
    content: string,
    taggedAgents: string[],
    files: File[]
  ) => {
    sendMessage(content, taggedAgents, files);
  };

  /**
   * Handle transcription request for an attachment.
   * Only enabled if user has transcribe permission.
   */
  const handleTranscribe = permissions.canUploadFiles
    ? (attachmentId: string) => transcribe(attachmentId)
    : undefined;

  // -------------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------------

  return (
    <>
      {/* Toggle button (always visible) */}
      <button
        type="button"
        className="chat-toggle-button"
        onClick={onToggle}
        title={isOpen ? 'Close chat' : 'Open chat'}
      >
        {/* Chat icon with message count badge */}
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
        </svg>
        {/* Message count badge */}
        {messages.length > 0 && (
          <span className="message-badge">{messages.length}</span>
        )}
      </button>

      {/* Sidebar panel */}
      <div className={`chat-sidebar ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="chat-header">
          <h3>Chat</h3>
          <div className="header-actions">
            {/* Clear button */}
            {messages.length > 0 && (
              <button
                type="button"
                className="header-button"
                onClick={clear}
                title="Clear chat"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
            {/* Stop button (when streaming) */}
            {isLoading && (
              <button
                type="button"
                className="header-button stop"
                onClick={stop}
                title="Stop generating"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="6" width="12" height="12" />
                </svg>
              </button>
            )}
            {/* Close button */}
            <button
              type="button"
              className="header-button"
              onClick={onToggle}
              title="Close chat"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages list */}
        <div className="chat-messages">
          {messages.length === 0 ? (
            // Empty state
            <div className="empty-state">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
              </svg>
              <p>Start a conversation</p>
              <span>Ask me anything about this board or @ tag an agent</span>
            </div>
          ) : (
            // Message list
            <>
              {messages.map((message) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  onTranscribe={handleTranscribe}
                />
              ))}
              {/* Auto-scroll anchor */}
              <div ref={messagesEndRef} />
            </>
          )}

          {/* Error display */}
          {error && (
            <div className="error-message">
              <span>Error: {error}</span>
            </div>
          )}
        </div>

        {/* Input area (gated by permissions) */}
        {permissions.canAccessChat ? (
          <ChatInput
            onSend={handleSend}
            disabled={isLoading}
            canUpload={permissions.canUploadFiles}
          />
        ) : (
          <div className="no-permission">
            You don't have permission to chat on this board.
          </div>
        )}
      </div>

      {/* Inline styles for chat sidebar */}
      <style>{`
        .chat-toggle-button {
          position: fixed;
          bottom: 24px;
          right: 24px;
          width: 56px;
          height: 56px;
          border: none;
          border-radius: 50%;
          background: var(--color-accent);
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px var(--color-accent-shadow);
          transition: all 0.2s;
          z-index: 100;
        }

        .chat-toggle-button:hover {
          background: var(--color-accent-hover);
          transform: scale(1.05);
        }

        .chat-toggle-button svg {
          width: 28px;
          height: 28px;
        }

        .message-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          border-radius: 10px;
          background: var(--color-error);
          color: white;
          font-size: 11px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .chat-sidebar {
          position: fixed;
          top: 0;
          right: -400px;
          width: 400px;
          height: 100vh;
          background: var(--color-surface);
          border-left: 1px solid var(--color-border);
          display: flex;
          flex-direction: column;
          transition: right 0.3s ease;
          z-index: 99;
          box-shadow: -4px 0 12px rgba(0, 0, 0, 0.1);
        }

        .chat-sidebar.open {
          right: 0;
        }

        .chat-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          border-bottom: 1px solid var(--color-border);
          background: var(--color-surface-muted);
        }

        .chat-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--color-text);
        }

        .header-actions {
          display: flex;
          gap: 4px;
        }

        .header-button {
          width: 32px;
          height: 32px;
          border: none;
          border-radius: 6px;
          background: transparent;
          color: var(--color-text-secondary);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .header-button:hover {
          background: var(--color-border);
          color: var(--color-text);
        }

        .header-button.stop {
          color: var(--color-error);
        }

        .header-button.stop:hover {
          background: var(--color-error-soft);
        }

        .header-button svg {
          width: 18px;
          height: 18px;
        }

        .chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 0;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          padding: 32px;
          text-align: center;
          color: var(--color-text-secondary);
        }

        .empty-state svg {
          width: 48px;
          height: 48px;
          color: var(--color-border-strong);
          margin-bottom: 16px;
        }

        .empty-state p {
          margin: 0 0 8px;
          font-size: 16px;
          font-weight: 500;
          color: var(--color-text-secondary);
        }

        .empty-state span {
          font-size: 13px;
        }

        .error-message {
          padding: 8px 16px;
          margin: 8px 16px;
          background: var(--color-error-soft);
          border-radius: 8px;
          color: var(--color-error);
          font-size: 13px;
        }

        .no-permission {
          padding: 16px;
          text-align: center;
          color: var(--color-text-secondary);
          font-size: 13px;
          border-top: 1px solid var(--color-border);
        }

        @media (max-width: 900px) {
          .chat-sidebar {
            width: 100%;
            right: -100%;
          }

          .chat-toggle-button {
            width: 48px;
            height: 48px;
            bottom: 16px;
            right: 16px;
          }
        }
      `}</style>
    </>
  );
}
